import type { RouteRecordRaw } from 'vue-router';
import { buildFullRoutePath } from '@/utils/routePathSync';

/** Uma linha do payload de `POST /routes/sync` (catálogo global de rotas de permissão). */
export interface RotaDoSync {
    path: string;
    name?: string;
    title?: string;
    requiresAuth?: boolean;
    requiresAdminMaster?: boolean;
}

/**
 * Achata a árvore do router no payload do sync de rotas (`authStore.syncFrontendRoutes`).
 *
 * Desce UM nível (filhos da rota de layout), como sempre desceu: o `syncRoutes` do backend
 * DESATIVA toda rota do catálogo que não vier aqui, e também REATIVA/CRIA toda que vier. Descer
 * mais fundo por padrão poria no catálogo (e reativaria) as rotas netas do Hub inteiro, que hoje
 * ficam de fora de propósito.
 *
 * Exceção EXPLÍCITA: um filho com `meta.syncFilhos: true` também manda os PRÓPRIOS filhos (netos
 * da rota de layout), com o path completo. É o caso das seções do Controle de Acesso
 * (`/seguranca/controle-acesso/<seção>`): cada uma é uma rota de permissão e mora dentro da casca
 * `ControleAcessoPage`. Sem isto, o primeiro login de admin master as desativava e o RH/TI perdia
 * o acesso (revisão do #466, B2).
 */
export function rotasDoSync(arvores: RouteRecordRaw[][]): RotaDoSync[] {
    const out: RotaDoSync[] = [];

    for (const routeArray of arvores) {
        for (const parentRoute of routeArray) {
            const parentPath = parentRoute.path;
            const parentAuth = parentRoute.meta?.requiresAuth;
            const requiresAuth = parentAuth !== undefined ? Boolean(parentAuth) : true;

            if ('children' in parentRoute && Array.isArray(parentRoute.children)) {
                const empurrar = (r: RouteRecordRaw, fullPath: string, herdadoAdminMaster: unknown) => {
                    const adminMaster = r.meta?.requiresAdminMaster ?? herdadoAdminMaster;
                    out.push({
                        path: fullPath,
                        name: r.name?.toString(),
                        title: r.meta?.title?.toString() || r.name?.toString(),
                        requiresAuth,
                        requiresAdminMaster: adminMaster !== undefined ? Boolean(adminMaster) : false,
                    });
                };
                for (const child of parentRoute.children) {
                    // Ignorar redirects e catch-all
                    if (child.redirect || child.path === ':pathMatch(.*)*') continue;

                    // Construir path completo (buildFullRoutePath já trata childPath vazio)
                    const fullPath = buildFullRoutePath(parentPath, child.path ?? '');
                    empurrar(child, fullPath, parentRoute.meta?.requiresAdminMaster);

                    if (child.meta?.syncFilhos === true && Array.isArray(child.children)) {
                        const herdado = child.meta?.requiresAdminMaster ?? parentRoute.meta?.requiresAdminMaster;
                        for (const neto of child.children) {
                            if (neto.redirect || neto.path === ':pathMatch(.*)*') continue;
                            empurrar(neto, buildFullRoutePath(fullPath, neto.path ?? ''), herdado);
                        }
                    }
                }
            } else if (!('redirect' in parentRoute) || !parentRoute.redirect) {
                // Rota sem children (rara, mas possível)
                out.push({
                    path: parentPath,
                    name: 'name' in parentRoute ? parentRoute.name?.toString() : undefined,
                    title: parentRoute.meta?.title?.toString(),
                    requiresAuth,
                    requiresAdminMaster: parentRoute.meta?.requiresAdminMaster !== undefined ? Boolean(parentRoute.meta.requiresAdminMaster) : false,
                });
            }
        }
    }
    return out;
}
